/* Just Silver - motion and interaction layer (pairs with js-motion.css).

   Everything here is an enhancement; the page works and reads the same
   without it. Content is only ever hidden for a reveal when it starts below
   the fold and IntersectionObserver is available, and a safety timer shows
   everything if the observer never reports. Reveals and image fades are
   skipped in the theme editor and for prefers-reduced-motion. */
(function () {
  'use strict';

  var d = document;
  var w = window;
  var root = d.documentElement;
  var reduce = w.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var design = !!(w.Shopify && w.Shopify.designMode);
  var desktop = w.matchMedia('(min-width: 48em)');
  var each = function (list, fn) { Array.prototype.forEach.call(list, fn); };

  root.classList.add('jsm');

  /* 1. Header: separate it from the content once the page has scrolled */
  var scrolled = null;
  var onScroll = function () {
    var s = (w.scrollY || w.pageYOffset || 0) > 8;
    if (s !== scrolled) {
      scrolled = s;
      root.classList.toggle('jsm-scrolled', s);
    }
  };
  w.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* 2. Scroll reveals. Containers are listed before anything they contain,
     and an element inside an already-marked one is skipped, so nothing
     moves twice. [selector, kind, stagger siblings] */
  var targets = [
    ['.ct__head, .bsf__head, .sbp__head', 'up', false],
    ['.ct__chipwrap, .ct__panels', 'up', false],
    ['.bsf__fan, .bsf__foot', 'up', false],
    ['.sbp__cell', 'up', true],
    ['.image-hotspots .media-banner__content-inner', 'up', false],
    ['.split-card .media-banner__content-inner', 'up', false],
    ['.stacked-images-with-text__text-wrapper', 'up', false],
    ['.media-grid .content-header', 'up', false],
    ['.multicolumn .slider-component__inner', 'up', false],
    ['.rich-text .rich-text__heading-group', 'up', false],
    ['.rich-text__collapsible', 'up', true],
    ['.image-hotspots .media-wrapper, .split-card .media-wrapper', 'zoom', false]
  ];

  var reveal = function (el) { el.classList.add('is-revealed'); };

  /* 2 and 3 share one measuring pass. They used to measure each element and
     mark it straight away, so every mark made the browser lay the page out
     again before the next measurement: on a collection page with 40 product
     photos that was 40 full layouts, about 2.8 s of a mid-range phone's time.
     Now everything is measured first, in one layout, and marked afterwards. */
  var measureThenMark = function () {
    var fold = w.innerHeight * 0.92;
    var vh = w.innerHeight;

    // read: candidates for reveals
    var cands = [];
    var seen = [];
    if (!reduce && !design && 'IntersectionObserver' in w) {
      targets.forEach(function (t) {
        each(d.querySelectorAll(t[0]), function (el) {
          if (seen.indexOf(el) > -1) return;
          seen.push(el);
          cands.push({ el: el, t: t, r: el.getBoundingClientRect() });
        });
      });
    }

    // read: lazy images below the fold
    var imgs = [];
    if (!reduce && !design) {
      each(d.querySelectorAll('main img[loading="lazy"]'), function (img) {
        if (img.complete || img.closest('.hf')) return;
        imgs.push({ img: img, r: img.getBoundingClientRect() });
      });
    }

    // write: reveals. An element inside one already marked to rise is left
    // alone, so nothing moves twice (containers are listed first).
    var marked = [];
    var inMarked = function (el) {
      for (var p = el.parentElement; p; p = p.parentElement) {
        if (p.getAttribute && p.getAttribute('data-jsm-reveal') === 'up') return true;
      }
      return false;
    };
    cands.forEach(function (c) {
      var el = c.el, t = c.t, r = c.r;
      if (el.hasAttribute('data-jsm-reveal')) return;
      if (t[1] === 'up' && inMarked(el)) return;
      if (r.top < fold || (r.width === 0 && r.height === 0)) return;
      if (t[2]) {
        var sibs = Array.prototype.filter.call(el.parentElement.children, function (x) {
          return x.matches(t[0]);
        });
        el.style.setProperty('--jsm-i', String(Math.min(sibs.indexOf(el), 5)));
      }
      el.setAttribute('data-jsm-reveal', t[1]);
      marked.push(el);
    });

    if (marked.length) {
      var heard = false;
      var io = new IntersectionObserver(function (entries) {
        heard = true;
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            reveal(e.target);
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      marked.forEach(function (el) { io.observe(el); });

      // if the observer never reports (some embedded or background views),
      // show everything rather than leave content hidden
      setTimeout(function () {
        if (!heard) { marked.forEach(reveal); io.disconnect(); }
      }, 3000);
      w.addEventListener('beforeprint', function () { marked.forEach(reveal); });
    }

    // write: lazy images fade in when they arrive
    imgs.forEach(function (c) {
      var img = c.img, r = c.r;
      if (img.complete) return;
      if (r.height > 0 && r.top < vh) return;
      img.setAttribute('data-jsm-img', 'loading');
      img.addEventListener('load', function () {
        img.setAttribute('data-jsm-img', 'in');
        img.addEventListener('animationend', function () {
          img.removeAttribute('data-jsm-img');
        }, { once: true });
      }, { once: true });
      img.addEventListener('error', function () {
        img.removeAttribute('data-jsm-img');
      }, { once: true });
    });
  };
  measureThenMark();

  /* 4. Hero on phones: the banners are a swipe row there, so the sparkle
     dots follow the swipe, and tapping a dot scrolls to its banner */
  each(d.querySelectorAll('hero-fan'), function (hf) {
    var track = hf.querySelector('.hf__track');
    var items = hf.querySelectorAll('.hf__item');
    var dots = hf.querySelectorAll('.hf__dot');
    if (!track || !dots.length || !items.length) return;
    var current = -1;

    var centreOf = function (el) {
      var r = el.getBoundingClientRect();
      return r.left + r.width / 2;
    };
    var nearest = function () {
      var mid = centreOf(track);
      var best = 0;
      var bestD = Infinity;
      each(items, function (it, i) {
        var dd = Math.abs(centreOf(it) - mid);
        if (dd < bestD) { bestD = dd; best = i; }
      });
      return best;
    };
    var sync = function () {
      if (desktop.matches) return;
      var i = nearest();
      if (i === current) return;
      current = i;
      each(dots, function (dot, k) {
        dot.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });
    };

    track.addEventListener('scroll', sync, { passive: true });
    each(dots, function (dot) {
      dot.addEventListener('click', function () {
        if (desktop.matches) return;
        var it = items[+dot.getAttribute('data-index')];
        if (!it) return;
        track.scrollTo({
          left: track.scrollLeft + centreOf(it) - centreOf(track),
          behavior: reduce ? 'auto' : 'smooth'
        });
      });
    });
    sync();
  });

  /* 5. Category chips: fade the edge that has more chips behind it */
  each(d.querySelectorAll('.ct__chips'), function (el) {
    var update = function () {
      var max = el.scrollWidth - el.clientWidth;
      var v = [];
      if (max > 4) {
        if (el.scrollLeft > 4) v.push('start');
        if (el.scrollLeft < max - 4) v.push('end');
      }
      var s = v.join(' ');
      if (el.getAttribute('data-jsm-fade') !== s) el.setAttribute('data-jsm-fade', s);
    };
    el.addEventListener('scroll', update, { passive: true });
    w.addEventListener('resize', update);
    update();
  });

  /* 6. Cart count pops when it changes. The header re-renders the count, so
     watch the header rather than the badge itself. */
  var header = d.querySelector('.header__right') || d.querySelector('.header');
  if (header && !reduce) {
    var countText = function () {
      var c = d.querySelector('.header__cart-count');
      return c ? c.textContent.trim() : '';
    };
    var last = countText();
    new MutationObserver(function () {
      var now = countText();
      if (now === last) return;
      last = now;
      each(d.querySelectorAll('.header__cart-count'), function (c) {
        c.classList.remove('jsm-bump');
        void c.offsetWidth;
        c.classList.add('jsm-bump');
        c.addEventListener('animationend', function () {
          c.classList.remove('jsm-bump');
        }, { once: true });
      });
    }).observe(header, { childList: true, subtree: true, characterData: true });
  }
})();
